const ENV_API_BASE: string = import.meta.env.VITE_API_URL

export const DEV_API_TARGETS = {
  local: "https://localhost:5001/api",
  test: "https://dashboard-api-test.livelyriver-a7697426.eastus.azurecontainerapps.io/api",
} as const

export type DevApiTarget = keyof typeof DEV_API_TARGETS

const DEV_API_TARGET_STORAGE_KEY = "dev:apiTarget"

// Dev-only, gated behind import.meta.env.DEV -- Vite eliminates this
// entirely from production builds, so it can't affect a real deployment.
export function getDevApiTarget(): DevApiTarget | null {
  if (!import.meta.env.DEV) return null
  // Guards against module-eval-time callers (client.ts, getOpenApiSpec.ts
  // compute API_ORIGIN once at import time) running before jsdom's
  // localStorage global is attached in tests -- always defined in a real
  // dev browser session.
  if (typeof localStorage === "undefined") return null
  const stored = localStorage.getItem(DEV_API_TARGET_STORAGE_KEY)
  return stored === "local" || stored === "test" ? stored : null
}

export function setDevApiTarget(target: DevApiTarget | null): void {
  if (!import.meta.env.DEV) return
  if (target) {
    localStorage.setItem(DEV_API_TARGET_STORAGE_KEY, target)
  } else {
    localStorage.removeItem(DEV_API_TARGET_STORAGE_KEY)
  }
  // Simplest way to avoid mixing cached data fetched from two different
  // backends -- a clean reload means every query refetches from scratch
  // against whichever target is now active.
  window.location.reload()
}

// Every existing call site does `${API_BASE}/...` -- an object with a
// toString() keeps all of those working unchanged (template literals call
// toString() on interpolation, evaluated fresh on every request) while
// still letting the dev target be switched at runtime without rebuilding.
// The couple of places that call string methods directly on this
// (getOpenApiSpec.ts, client.ts) need `String(API_BASE)` first.
export const API_BASE = {
  toString(): string {
    if (import.meta.env.DEV) {
      const target = getDevApiTarget()
      if (target) return DEV_API_TARGETS[target]
    }
    return ENV_API_BASE
  },
}

export const PAGE_PATH_MAP: { path: string; title: string }[] = [
  { path: "/", title: "Home" },
  { path: "/kpis", title: "KPIs" },
  { path: "/incentives", title: "Incentives" },
  { path: "/dashboards/BCO", title: "BC Organics Dashboard" },
  { path: "/dashboards/Digesters", title: "BCO Digester Feeding Detail" },
  { path: "/dashboards/Digesters/Metrics", title: "BCO Digester Metrics" },
  { path: "/dashboards/SPL", title: "Sagepoint Logistics Farm Dashboard" },
  { path: "/dashboards/SPL/Balances", title: "Farm Balances" },
  { path: "/dashboards/For-Hire", title: "Sagepoint Logistics For Hire Dashboard" },
  { path: "/dashboards/For-Hire/balance", title: "Sagepoint Logistics Load Balance Board" },
  { path: "/dashboards/PLTH", title: "Plumb Thicket Dashboard" },
  { path: "/dashboards/LWRN", title: "Lawrence Dashboard" },
  { path: "/dashboards/PEPL", title: "Peoples Dashboard" },
  { path: "/datapoints/defs/:id", title: "Edit Data Point" },
  { path: "/datapoints/defs/new/:tag", title: "New Data Point" },
  { path: "/datapoints/defs", title: "Data Point Definitions" },
  { path: "/datapoints/reports", title: "Data Point Reports" },
  { path: "/datapoints/report-runs", title: "Data Point Report Runs" },
  { path: "/datapoints/reports/new", title: "New Data Point Report" },
  { path: "/datapoints/reports/:id", title: "Edit Data Point Report" },
  { path: "/admin/kpi", title: "KPI Admin" },
]
